/**
 * Motion Animation Variants
 * Premium Playful design system animations
 *
 * Timing: 600ms with cubic-bezier(0.22, 1, 0.36, 1) for scroll reveals
 * Stagger: 100ms between children
 * Hover: 200ms ease-out
 *
 * ANTI-DEAD-ZONE RULE (apps/landing/DESIGN.md): content renders VISIBLE by
 * default — "hidden" states keep opacity 1 and only carry a transform offset,
 * so a section whose IntersectionObserver never fires (bots, social crawlers,
 * JS hiccups, viewport geometry) is still fully readable. The 2026-07-02 audit
 * found multi-viewport blank stretches on prod caused by opacity-0 gating with
 * a -100px trigger margin. Motion enhances; it must never gate content.
 */

import type { TargetAndTransition, Variants, Transition } from 'motion/react';

// Premium easing curve - fast start, smooth end
const premiumEase = [0.22, 1, 0.36, 1] as const;

// Standard transition for scroll reveals
const revealTransition: Transition = {
  duration: 0.6,
  ease: premiumEase,
};

// Fade in from bottom - primary scroll reveal animation
export const fadeInUp: Variants = {
  hidden: {
    opacity: 1,
    y: 24,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: revealTransition,
  },
};

// Fade in from right - for alternating layouts
export const fadeInRight: Variants = {
  hidden: {
    opacity: 1,
    x: 24,
  },
  visible: {
    opacity: 1,
    x: 0,
    transition: revealTransition,
  },
};

// Fade in with scale - for hero elements
export const fadeInScale: Variants = {
  hidden: {
    opacity: 1,
    scale: 0.97,
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: revealTransition,
  },
};

// Container for staggered children
export const staggerContainer: Variants = {
  hidden: { opacity: 1 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
};

// Fast stagger for lists
export const fastStaggerContainer: Variants = {
  hidden: { opacity: 1 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05,
      delayChildren: 0.05,
    },
  },
};

// Child variant for staggered animations
export const staggerChild: Variants = {
  hidden: {
    opacity: 1,
    y: 16,
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: premiumEase,
    },
  },
};

// Hover scale effect for buttons/cards
const hoverScale = {
  scale: 1.02,
  transition: { duration: 0.2, ease: 'easeOut' },
};

// Tap effect for buttons
const tapScale = {
  scale: 0.98,
};

// Card hover with shadow lift
export const cardHover: TargetAndTransition = {
  y: -4,
  transition: { duration: 0.2, ease: 'easeOut' },
};

// Viewport settings for scroll triggers
export const defaultViewport = {
  once: true,
  margin: '0px',
};

// Reduced motion check
export const getReducedMotionVariants = (variants: Variants): Variants => ({
  hidden: { opacity: 1 },
  visible: {
    opacity: 1,
    transition: { duration: 0 },
  },
});
